import React, { useId, useState } from "react";
import { MdCheckCircle, MdExpandMore } from "react-icons/md";
import Badge from "components/badge/Badge";
import Card from "components/card";
import { archetypeDescriptions } from "data/archetypeDescriptions";
import { ARCHETYPE_KEYS, getArchetype } from "lib/archetypes";

const capitalize = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : "");

const labelOf = (key) => getArchetype(key)?.label || capitalize(key);

const Disclosure = ({ title, children }) => {
  const [open, setOpen] = useState(false);
  const panelId = useId();

  return (
    <div className="border-b border-line/60 last:border-b-0">
      <h3>
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          aria-controls={panelId}
          className="flex w-full items-center justify-between gap-3 px-6 py-4 text-left text-sm font-semibold text-fg transition-colors hover:bg-surface-subtle focus-ring"
        >
          {title}
          <MdExpandMore
            aria-hidden="true"
            className={`h-5 w-5 shrink-0 text-fg-muted transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
      </h3>
      {open && (
        <div id={panelId} className="px-6 pb-4 text-sm leading-relaxed text-fg-muted">
          {children}
        </div>
      )}
    </div>
  );
};

const BulletList = ({ items }) => (
  <ul className="list-inside list-disc space-y-1">
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

const NEXT_STEPS = [
  "Your leadership team will review your profile and assessment results.",
  "They may reach out to discuss ministry opportunities that align with your strengths.",
  "You can close this page anytime. Your results have been saved.",
];

const AssessmentResults = ({ results, organizationName, userName }) => {
  const { percentages, primary, secondary } = results;
  const primaryArchetype = getArchetype(primary);
  const primaryDesc = archetypeDescriptions[primary] || {};
  const PrimaryIcon = primaryDesc.icon;
  const displayName = capitalize(userName);

  const ranked = ARCHETYPE_KEYS.filter((key) => percentages[key] !== undefined).sort(
    (a, b) => (percentages[b] || 0) - (percentages[a] || 0)
  );

  const hasCommunication = primaryDesc.communicationDo || primaryDesc.communicationDont;

  return (
    <div className="flex flex-col gap-5">
      <Card padding="md" className="items-center text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-success-subtle text-success-fg">
          <MdCheckCircle aria-hidden="true" className="h-7 w-7" />
        </span>
        <h1 className="mt-3 text-title">Assessment Complete</h1>
        <p className="mt-2 text-sm leading-relaxed text-fg">
          {displayName && <span className="font-medium">Great job, {displayName}. </span>}
          Your profile has been saved and your ministry archetype has been calculated.
        </p>
        {organizationName && (
          <p className="mt-2 text-sm text-fg-muted">
            {organizationName} can now better understand how to support you in your ministry journey.
          </p>
        )}
      </Card>

      <Card padding="md" className="items-center text-center">
        {PrimaryIcon && (
          <span
            className={`flex h-16 w-16 items-center justify-center rounded-full ${
              primaryArchetype?.badgeClass || "bg-surface-subtle text-fg"
            }`}
          >
            <PrimaryIcon aria-hidden="true" className="h-7 w-7" />
          </span>
        )}
        <p className="mt-4 text-xs font-medium uppercase tracking-wide text-fg-muted">
          Your Primary Archetype
        </p>
        <h2 className="mt-1 text-title">{primaryDesc.name || labelOf(primary)}</h2>
        {primaryDesc.shortDescription && (
          <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-fg-muted">
            {primaryDesc.shortDescription}
          </p>
        )}
        {secondary && (
          <p className="mt-4 inline-flex items-center gap-2 text-xs text-fg-muted">
            Secondary:
            <Badge tone="archetype" archetype={secondary}>
              {archetypeDescriptions[secondary]?.name || labelOf(secondary)}
            </Badge>
          </p>
        )}
      </Card>

      <Card padding="md">
        <h2 className="text-heading">Your Archetype Scores</h2>
        <ul className="mt-4 flex flex-col gap-3">
          {ranked.map((key) => {
            const archetype = getArchetype(key);
            const Icon = archetypeDescriptions[key]?.icon;
            const percent = Math.round(percentages[key] || 0);
            const isPrimary = key === primary;

            return (
              <li key={key}>
                <div className="mb-1 flex items-center justify-between gap-3 text-xs">
                  <span
                    className={`flex items-center gap-2 ${
                      isPrimary ? "font-bold text-fg" : "font-medium text-fg-muted"
                    }`}
                  >
                    {Icon && <Icon aria-hidden="true" className="h-3 w-3" />}
                    {archetypeDescriptions[key]?.name || labelOf(key)}
                  </span>
                  <span className="font-medium text-fg-muted">{percent}%</span>
                </div>
                <div className="h-2.5 w-full overflow-hidden rounded-full bg-surface-subtle" aria-hidden="true">
                  <div
                    className="h-full rounded-full transition-[width] duration-700 ease-out"
                    style={{ width: `${percent}%`, backgroundColor: archetype?.chartColor }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      </Card>

      {(primaryDesc.strengths ||
        primaryDesc.idealRoles ||
        hasCommunication ||
        primaryDesc.burnoutWarnings ||
        primaryDesc.managerPlaybook) && (
        <Card className="overflow-hidden">
          {primaryDesc.strengths && (
            <Disclosure title="Strengths">
              <BulletList items={primaryDesc.strengths} />
            </Disclosure>
          )}
          {primaryDesc.idealRoles && (
            <Disclosure title="Ideal Roles">
              <BulletList items={primaryDesc.idealRoles} />
            </Disclosure>
          )}
          {hasCommunication && (
            <Disclosure title="Communication Style">
              {primaryDesc.communicationDo && (
                <div className="mb-3">
                  <p className="mb-1 font-medium text-fg">Do:</p>
                  <BulletList items={primaryDesc.communicationDo} />
                </div>
              )}
              {primaryDesc.communicationDont && (
                <div>
                  <p className="mb-1 font-medium text-fg">Don't:</p>
                  <BulletList items={primaryDesc.communicationDont} />
                </div>
              )}
            </Disclosure>
          )}
          {primaryDesc.burnoutWarnings && (
            <Disclosure title="Burnout Warnings">
              <BulletList items={primaryDesc.burnoutWarnings} />
            </Disclosure>
          )}
          {primaryDesc.managerPlaybook && (
            <Disclosure title="Manager Playbook">
              <BulletList items={primaryDesc.managerPlaybook} />
            </Disclosure>
          )}
        </Card>
      )}

      <Card padding="md">
        <h2 className="text-heading">What's Next?</h2>
        <ol className="mt-3 flex flex-col gap-3 text-sm text-fg-muted">
          {NEXT_STEPS.map((step, index) => (
            <li key={step} className="flex items-start gap-3">
              <span
                aria-hidden="true"
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-bold text-brand-700 dark:bg-brand-900/40 dark:text-brand-200"
              >
                {index + 1}
              </span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
      </Card>
    </div>
  );
};

export default AssessmentResults;
